import React, { useEffect, useState } from "react";
import {
  Button,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
  Paper,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { getPedidosCatalogo } from "../../services/api";

const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
};

const formatCurrency = (value) => {
  if (value == null) return "-";
  return new Intl.NumberFormat("es-PY").format(value);
};

const PedidosCatalogo = () => {
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalPedido, setModalPedido] = useState(null);

  useEffect(() => {
    const fetchPedidos = async () => {
      try {
        const response = await getPedidosCatalogo();
        const data = response.data?.pedidos ?? [];
        setPedidos(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPedidos();
  }, []);

  const handleVerPedido = (pedido) => setModalPedido(pedido);
  const handleCerrarModal = () => setModalPedido(null);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container style={{ marginLeft: "0", maxWidth: "100%" }}>
      <h1 style={{ marginTop: 0 }}>Catálogo Delivery - Pedidos</h1>

      {error ? (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <ReportProblemOutlinedIcon style={{ fontSize: 50, color: "gray" }} />
          <Typography variant="h6" color="textSecondary">
            Error al cargar los pedidos.
          </Typography>
        </Box>
      ) : pedidos.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <HelpOutlineIcon style={{ fontSize: 50, color: "gray" }} />
          <Typography variant="h6" color="textSecondary">
            No hay pedidos
          </Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: "1px solid lightgray",
            borderRadius: "8px",
            backgroundColor: "#fff",
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: "bold" }}>ID</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Cliente</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Fecha</TableCell>
                <TableCell style={{ fontWeight: "bold" }} align="right">
                  Total
                </TableCell>
                <TableCell style={{ fontWeight: "bold" }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pedidos.map((pedido) => (
                <TableRow
                  key={pedido.id}
                  sx={{
                    backgroundColor: "#fff",
                    "&:hover": { backgroundColor: "#f0f0f0" },
                  }}
                >
                  <TableCell>{pedido.id}</TableCell>
                  <TableCell>
                    {pedido.cliente?.nombreCompleto ?? "-"}
                  </TableCell>
                  <TableCell>{formatDate(pedido.fecha_pedido)}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(pedido.totales?.total)}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleVerPedido(pedido)}
                      size="small"
                      title="Ver pedido"
                    >
                      <VisibilityOutlinedIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={Boolean(modalPedido)}
        onClose={handleCerrarModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        {modalPedido && (
          <>
            <DialogTitle>Pedido #{modalPedido.id}</DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Cliente
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {modalPedido.cliente?.nombreCompleto}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  C.I. {modalPedido.cliente?.cedula} · {modalPedido.cliente?.telefono}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {modalPedido.cliente?.direccion}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Productos
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Producto</TableCell>
                      <TableCell align="right">Cant.</TableCell>
                      <TableCell>Unidad</TableCell>
                      <TableCell align="right">P. unit.</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(modalPedido.productos ?? []).map((p, idx) => (
                      <React.Fragment key={idx}>
                        <TableRow>
                          <TableCell>{p.nombre}</TableCell>
                          <TableCell align="right">{p.cantidad}</TableCell>
                          <TableCell>{p.unidad}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(p.precioUnitario)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(p.total)}
                          </TableCell>
                        </TableRow>
                        {p.observacion && (
                          <TableRow>
                            <TableCell colSpan={5} sx={{ py: 0.5, borderBottom: "1px solid rgba(224, 224, 224, 1)" }}>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{
                                  fontStyle: "italic",
                                  display: "block",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  lineHeight: 1.2,
                                  pb: 1,
                                }}
                              >
                                {p.observacion}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Typography variant="body2" color="textSecondary">
                  Subtotal:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatCurrency(modalPedido.totales?.subtotal)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Typography variant="body1" fontWeight="bold">
                  Total:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatCurrency(modalPedido.totales?.total)}
                </Typography>
              </Box>
              <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 1 }}>
                Fecha: {formatDate(modalPedido.fecha_pedido)}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
              <Button onClick={handleCerrarModal} variant="contained">
                Cerrar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default PedidosCatalogo;
